import { reportError } from '@pypestream/utils';

/**
 * Reports a failure inside an invoke that goes on to RESOLVE.
 *
 * The reporter wired into `createInvokablePromise` fires on `onError`, so it
 * only ever sees an invoke that rejects. An invoke that catches and resolves —
 * to degrade gracefully, to stay best-effort, or to keep a modal open — is
 * invisible to it by construction, and those are precisely the paths where the
 * user is given a result that looks complete. `load-bot-pypes` is the sharpest
 * of them: it answers with the PREVIOUS snapshot, so the channels table shows
 * yesterday's list as though it were today's, with nothing on screen to say so.
 *
 * Deliberately NOT a reason to start rejecting. Each of these paths chose to
 * resolve for a stated reason — `load-bot-pypes` would otherwise make the table
 * disappear on a flaky read, and the best-effort blocks run after the caller's
 * promise has already settled. This makes the choice observable; it does not
 * revisit it.
 *
 * Guarded, like every other reporter in these invokes: telemetry must not be
 * able to turn a degraded result into a broken one.
 */
export const reportDegradedLoad = (
  error: unknown,
  source: string,
  extra: Record<string, unknown> = {}
): void => {
  try {
    reportError(error, {
      source,
      tags: { app: 'pro-studio', degraded: 'true' },
      extra,
    });
  } catch {
    // Deliberately empty — see above.
  }
};
